// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/EtapasProducao.tsx
//  Cadastro de Etapas de Produção / roteiro (paridade desktop frm_cad_etapasProducao).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, GitMerge, Trash2, Save, Pencil } from 'lucide-react';

import {
  etapaProducaoService, type EtapaProducao,
} from '@/features/operacional/services/etapaProducaoService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';

export default function EtapasProducao() {
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen]   = useState(false);
  const [editId, setEditId]       = useState<number | null>(null);
  const [descricao, setDescricao] = useState('');
  const [referencia, setReferencia] = useState('');
  const [sequencia, setSequencia] = useState(0);
  const [inativo, setInativo]     = useState(false);
  const [observacao, setObservacao] = useState('');
  const [removendoId, setRemovendoId] = useState<number | null>(null);

  const { data: etapas = [], isLoading } = useQuery({
    queryKey: ['etapas-producao', 'todas'],
    queryFn: () => etapaProducaoService.getAll(true),
  });

  const reset = () => {
    setEditId(null); setDescricao(''); setReferencia('');
    setSequencia(0); setInativo(false); setObservacao('');
  };

  const abrirNovo = () => { reset(); setFormOpen(true); };
  const abrirEdicao = (e: EtapaProducao) => {
    setEditId(e.id); setDescricao(e.descricao ?? ''); setReferencia(e.referencia ?? '');
    setSequencia(e.sequencia); setInativo(e.isInativo); setObservacao(e.observacao ?? '');
    setFormOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!descricao.trim()) throw new Error('Informe a descrição da etapa');
      const payload = { descricao, referencia: referencia || undefined, sequencia, isInativo: inativo, observacao: observacao || undefined };
      return editId ? etapaProducaoService.update(editId, payload) : etapaProducaoService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['etapas-producao'] });
      toast.success(editId ? 'Etapa atualizada' : 'Etapa criada');
      setFormOpen(false); reset();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => etapaProducaoService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['etapas-producao'] });
      toast.success('Etapa removida');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitMerge className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Etapas de Produção</h1>
            <p className="text-sm text-muted-foreground">Roteiro / fases do processo produtivo.</p>
          </div>
        </div>
        <Button className="gap-2" onClick={abrirNovo}><Plus className="h-4 w-4" />Nova Etapa</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Carregando...</div>
          ) : etapas.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <GitMerge className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhuma etapa cadastrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right w-20">Seq.</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {etapas.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-right">{e.sequencia}</TableCell>
                    <TableCell className="font-medium">{e.descricao}</TableCell>
                    <TableCell>{e.referencia ?? '—'}</TableCell>
                    <TableCell>
                      {e.isInativo
                        ? <Badge className="bg-red-500/10 text-red-500">Inativa</Badge>
                        : <Badge className="bg-green-500/10 text-green-500">Ativa</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => abrirEdicao(e)} title="Editar">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemovendoId(e.id)} title="Remover">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={removendoId != null}
        onOpenChange={(o) => { if (!o) setRemovendoId(null); }}
        onConfirm={() => { if (removendoId != null) removeMutation.mutate(removendoId); setRemovendoId(null); }}
        title="Remover etapa"
        description="Tem certeza que deseja remover esta etapa de produção?"
      />

      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) reset(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Editar Etapa' : 'Nova Etapa'}</DialogTitle>
            <DialogDescription>Defina a etapa do roteiro de produção.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: Corte, Montagem, Acabamento" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Sequência</Label>
                <Input type="number" min={0} value={sequencia} onChange={(e) => setSequencia(Number(e.target.value))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observação</Label>
              <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <input id="etapaInativa" type="checkbox" checked={inativo}
                onChange={(e) => setInativo(e.target.checked)} className="h-4 w-4 rounded border border-input" />
              <Label htmlFor="etapaInativa" className="cursor-pointer">Inativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormOpen(false); reset(); }}>Cancelar</Button>
            <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4" />Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
